/**
 * Whether Sentry may send anything at all, and under which environment name.
 *
 * WHY THIS EXISTS
 *
 * In the 24 hours to 2026-08-07, all 20 new issues in the `foto-nextjs` project
 * came from a developer's laptop. Every culprit was a `/dev/*` route, every one
 * had 0 users, and the stack traces named a local worktree path under
 * `.claude/worktrees/`. They were hot-reload artefacts — `cargoFloorShapes is
 * not defined`, `wheelRef is not a function` — thrown by modules being edited
 * mid-render. Two of them arrived tagged `environment:production` and were
 * therefore indistinguishable, in any filter or alert, from an error a paying
 * customer hit.
 *
 * The cause was a single fallback. The client init read
 *
 *     environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || 'production'
 *
 * and that variable is supplied ONLY as a Docker build arg (see the Dockerfile
 * and .github/workflows/docker-build-push.yml, which set it to `stage` and
 * `production` respectively). It is in no `.env` file, so on a developer's
 * machine it is undefined and the `||` names the laptop "production". The
 * server and edge inits did not set `environment` at all, which is worse in the
 * same direction: the SDK's own default for an unset environment is
 * "production", so local SSR errors landed there too.
 *
 * The fix is not to rename the local environment — a developer's HMR errors
 * have no diagnostic value to this shop at any tag — but to stop sending them.
 *
 * WHY NODE_ENV AND NOT A NEW VARIABLE
 *
 * Requiring a developer to set something is a fix that fails open: whoever
 * forgets it pollutes production again, which is precisely the state we are in.
 * `NODE_ENV` is set by Next itself and cannot be forgotten — `next dev` sets
 * `development`, `next build` / `next start` set `production` — and both the
 * stage and production images run `yarn build` then `yarn start` behind
 * `ENV NODE_ENV=production` (Dockerfile:110). So the deployed environments are
 * unaffected by construction, and no new variable has to be remembered.
 *
 * The hostname check is the second line, for the one case NODE_ENV misses: a
 * developer running a real production build locally (`yarn build && yarn
 * start`) to reproduce something. Its inputs are what the BROWSER sees, and no
 * customer ever reaches this app on a loopback or private-range address —
 * production is fotodelavnica.si and friends, stage is stage.fotodelavnica.si.
 *
 * Everything here is pure and takes its inputs explicitly, so the rule can be
 * tested without a bundler and without mutating process.env.
 */

/** The facts the decision is made from. */
export interface SentryRuntime {
  /** `process.env.NODE_ENV`, inlined at build time by Next. */
  nodeEnv: string | undefined;
  /** `process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT`, set only by the Docker build. */
  configuredEnvironment: string | undefined;
  /** `window.location.hostname` on the client; absent on the server/edge. */
  hostname?: string | null;
}

/** Loopback and "this machine" names. */
const LOOPBACK_HOSTNAMES = new Set([
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "::1",
  "[::1]",
]);

/**
 * Private (RFC1918) IPv4 ranges — a phone testing the dev server over the
 * office wifi arrives on one of these. A customer never does.
 */
const PRIVATE_IPV4 =
  /^(10\.\d{1,3}\.\d{1,3}\.\d{1,3}|192\.168\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3})$/;

/** Is this host one only a developer can be looking at? */
export function isLocalHostname(hostname: string | null | undefined): boolean {
  if (typeof hostname !== "string") return false;
  const raw = hostname.trim().toLowerCase();
  if (!raw) return false;
  if (LOOPBACK_HOSTNAMES.has(raw)) return true;
  // Strip a trailing :port, but never from a bare IPv6 literal — `::1` ends in
  // `:1` and would be mangled into `::`. window.location.hostname never carries
  // a port anyway; this only guards callers that pass a `host`.
  const host = raw.includes("::") ? raw : raw.replace(/:\d+$/, "");
  if (LOOPBACK_HOSTNAMES.has(host)) return true;
  // mDNS / the `.localhost` reserved TLD, both of which resolve to this machine.
  if (host.endsWith(".local") || host.endsWith(".localhost")) return true;
  return PRIVATE_IPV4.test(host);
}

/**
 * True when this process is a developer's machine rather than a deployment.
 *
 * Deliberately `!== "production"` rather than `=== "development"`: a build that
 * somehow reports neither is far more likely to be a local experiment than a
 * customer-facing deployment, and the cost of being wrong is asymmetric —
 * suppressing one local event costs nothing, admitting one more mislabelled
 * `environment:production` issue costs the shop a triage.
 */
export function isLocalDevelopment(runtime: SentryRuntime): boolean {
  if (runtime.nodeEnv !== "production") return true;
  return isLocalHostname(runtime.hostname);
}

/**
 * Should the SDK send events at all?
 *
 * Passed to `Sentry.init({ enabled })`, which is the switch that stops the
 * transport while leaving the whole `Sentry.*` API callable — every
 * `captureMessage` / `addBreadcrumb` in the app becomes a no-op instead of
 * needing a guard at each call site.
 */
export function shouldEnableSentry(runtime: SentryRuntime): boolean {
  return !isLocalDevelopment(runtime);
}

/**
 * The environment name to tag events with.
 *
 * Outside local development this is byte-for-byte the previous expression
 * (`NEXT_PUBLIC_SENTRY_ENVIRONMENT` or "production"), so stage and production
 * reporting is untouched. Locally it says "development" — belt and braces
 * behind `enabled: false`, so that if an event ever does escape (a Sentry
 * option we do not control, a future refactor) it is at least unambiguous.
 */
export function resolveSentryEnvironment(runtime: SentryRuntime): string {
  if (isLocalDevelopment(runtime)) return "development";
  return runtime.configuredEnvironment || "production";
}

/**
 * Read the real runtime. Kept apart from the rules above so the rules stay pure.
 *
 * `process.env.X` is written out in full on purpose: Next/Turbopack inlines
 * these by literal text substitution, and a computed lookup would survive into
 * the browser bundle as an access on an object that is not there.
 */
export function currentSentryRuntime(): SentryRuntime {
  return {
    nodeEnv: process.env.NODE_ENV,
    configuredEnvironment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
    hostname:
      typeof window === "undefined" ? undefined : window.location?.hostname,
  };
}
